'use client';

import type { ReactNode } from 'react';
import { useState } from 'react';
import type { ModalProps as MantineModalProps } from '@mantine/core';
import { Modal as MantineModal, useProps } from '@mantine/core';

import type { LockedProps } from '../types';
import { ModalFooterSlotProvider } from './modal-footer';
import classes from './modal.module.css';

/**
 * The body's two regions. State rather than a ref, because the footer element has to be a rendered
 * value for {@link ModalFooter} to re-render once it exists — and it lives in here rather than in
 * `Modal` so that second pass re-renders these two elements instead of all of Mantine's chrome.
 */
const ModalBodyRegions = ({ children }: { children: ReactNode }) => {
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);

  return (
    <>
      {/* A scrolling region whose content holds nothing focusable — a long block of text — is
          unreachable for a keyboard-only user unless the region itself takes focus. */}
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className={classes.scrollRegion} tabIndex={0}>
        <ModalFooterSlotProvider value={footerSlot}>{children}</ModalFooterSlotProvider>
      </div>

      <div className={classes.footer} ref={setFooterSlot} />
    </>
  );
};

/**
 * Mantine's ModalProps minus the {@link LockedProps} style overrides, and minus
 * `scrollAreaComponent`: it wraps the header and body in an element of its own, which collapses
 * the flex column the fixed chrome is built on — silently, since the modal still renders. The body
 * scrolls natively now, so nothing needs it. `stackId` goes too: `Modal` is built from Mantine's
 * parts, and only Mantine's own `Modal` joins a `Modal.Stack`.
 */
export type ModalProps = Omit<
  MantineModalProps,
  LockedProps | 'scrollAreaComponent' | 'stackId' | 'variant'
> & {
  /**
   * `dividerless` drops the rules under the header and over the action row, and closes up the
   * padding either side of them. Narrowed from Mantine's open `string`, so a typo doesn't compile.
   */
  variant?: 'dividerless';
  /**
   * A band across the top of the modal, above its title: `useConfirmDeleteBanner`'s ask, say. While
   * one shows, the rest of the modal is inert, so it's answered before anything else.
   */
  banner?: ReactNode;
};

const defaultProps = { centered: true, withCloseButton: true, withOverlay: true };

/**
 * ### Modal
 *
 * @description
 * Mantine's Modal minus per-call-site style overrides (see {@link LockedProps}). Centered by
 * default — Mantine's own default is top-aligned — which a caller can override with `centered`.
 * Body padding and the bold title come from the theme's Modal entry, not from here.
 *
 * `children` scroll between a fixed header and a fixed action row. Mantine has no footer slot, so
 * the row is an element rendered here as a sibling of the scrolling region, which {@link
 * ModalFooter} portals into — a modal with nothing to portal shows no footer at all. There is no
 * footer *prop*: for a Cancel/Save row use `ActionModal` from `@highlight-cp/core-ui-compositions`,
 * and for an imperative "are you sure?" prompt `useConfirmModal` / `useConfirmDeleteModal`.
 *
 * It is assembled from Mantine's `Modal.Root`/`Content`/`Header`/`Body` parts, as Mantine's own
 * `Modal` is, so a `banner` can sit above the header, where Mantine has no slot either. Like
 * Mantine's, it reads the theme's `Modal` defaults itself, since the root reads only its own.
 *
 * @example
 * ```tsx
 * const [opened, { open, close }] = useDisclosure(false);
 *
 * <Modal opened={opened} onClose={close} title={t('Survey Preview')} size="xl">
 *   <SurveyEmbed … />
 * </Modal>
 * ```
 *
 * @param props - {@link ModalProps}
 * @returns React component
 */
export const Modal = (props: ModalProps) => {
  const {
    banner,
    children,
    closeButtonProps,
    overlayProps,
    radius,
    title,
    withCloseButton,
    withOverlay,
    ...rootProps
  } = useProps('Modal', defaultProps, props);

  return (
    <MantineModal.Root radius={radius} {...rootProps}>
      {withOverlay && <MantineModal.Overlay {...overlayProps} />}
      <MantineModal.Content radius={radius}>
        {banner}
        {(title || withCloseButton) && (
          <MantineModal.Header inert={!!banner}>
            {title && <MantineModal.Title>{title}</MantineModal.Title>}
            {withCloseButton && <MantineModal.CloseButton {...closeButtonProps} />}
          </MantineModal.Header>
        )}
        <MantineModal.Body inert={!!banner}>
          <ModalBodyRegions>{children}</ModalBodyRegions>
        </MantineModal.Body>
      </MantineModal.Content>
    </MantineModal.Root>
  );
};
